import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META, STATUS_META, imageSrc } from "../lib/api";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from "date-fns";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

const DOTS = {
  draft: "#94A3B8", pending_approval: "#FBBF24", scheduled: "#A78BFA",
  posted: "#38BDF8", rejected: "#FB7185", approved: "#34D399",
};

export const ContentCalendar = () => {
  const [cursor, setCursor] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/posts");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const dated = posts.filter((p) => p.scheduled_at && (filter === "all" || p.platforms?.includes(filter)));

  const postsForDay = (d) => dated.filter((p) => {
    try { return isSameDay(parseISO(p.scheduled_at), d); } catch { return false; }
  });

  return (
    <div className="space-y-6" data-testid="calendar-view">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Content Calendar</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">{format(cursor, "MMMM yyyy")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 glass rounded-full p-1">
            {["all", ...Object.keys(PLATFORM_META)].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  filter === f ? "bg-indigo-500/20 text-indigo-200" : "text-slate-400 hover:text-slate-200"
                }`}>{f === "all" ? "All" : PLATFORM_META[f].label}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="cal-prev" onClick={() => setCursor(subMonths(cursor, 1))} className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-today" onClick={() => setCursor(new Date())} className="px-3 h-9 rounded-lg glass text-xs font-semibold text-slate-300 hover:text-white">Today</button>
            <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-3 sm:p-5">
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((d, i) => {
            const inMonth = isSameMonth(d, cursor);
            const today = isSameDay(d, new Date());
            const dayPosts = postsForDay(d);
            return (
              <div key={i} data-testid="calendar-day"
                className={`min-h-[92px] rounded-xl border p-1.5 sm:p-2 transition-colors ${
                  inMonth ? "bg-slate-900/50 border-slate-800" : "bg-transparent border-slate-800/40 opacity-50"
                } ${today ? "ring-1 ring-indigo-500/60" : ""}`}>
                <div className={`text-xs font-semibold mb-1 ${today ? "text-indigo-300" : "text-slate-400"}`}>{format(d, "d")}</div>
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map((p) => (
                    <button key={p.id} onClick={() => setSelected(p)} data-testid="calendar-post-chip"
                      className="w-full text-left flex items-center gap-1.5 px-1.5 py-1 rounded-md bg-slate-800/70 hover:bg-slate-700/70 transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: DOTS[p.status] }} />
                      <span className="text-[10px] text-slate-300 truncate">{p.topic}</span>
                    </button>
                  ))}
                  {dayPosts.length > 3 && <span className="text-[10px] text-slate-500 pl-1.5">+{dayPosts.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        {Object.entries(STATUS_META).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DOTS[k] }} />
            <span className="text-xs text-slate-400">{v.label}</span>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-heading pr-6">{selected.topic}</DialogTitle></DialogHeader>
              {selected.image_base64 && <img src={imageSrc(selected)} alt="" className="w-full rounded-xl" />}
              <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{selected.caption}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_META[selected.status]?.cls}`}>{STATUS_META[selected.status]?.label}</span>
                {selected.platforms?.map((pl) => (
                  <span key={pl} className="text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${PLATFORM_META[pl]?.color}22`, color: PLATFORM_META[pl]?.color }}>{PLATFORM_META[pl]?.label}</span>
                ))}
              </div>
              {selected.scheduled_at && <p className="text-xs text-slate-500">Scheduled: {new Date(selected.scheduled_at).toLocaleString()}</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
